import React, { useState } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Typography, CardContent, Card, Divider } from "@mui/material";
import FindInPageIcon from '@mui/icons-material/FindInPage';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Login_photo from "../../assets/images/BgImage.png";
import { useTheme } from "@emotion/react";
import StorageIcon from "../../assets/images/StorageIcon";
import VectorIcon from "../../assets/images/VectorIcon";
import { useNavigate } from "react-router-dom";
import { getPBIConfig } from "../../api";
import frontendSettings from "../../../frontend_settings.json";
import { useDispatch } from "react-redux";
import { useUIConfig } from "../../utils/uiConfigHelper";
import AttributesIcon from "../../assets/images/AttributesIcon";
import { showSnackbar } from "../../store/snackBarSlice";
import { UIConfig } from "../../api/models";


const Homepage: React.FC = () => {
  const theme: any = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get UI configuration from Redux store
  const uiConfig = useUIConfig();
  const featureFlags = uiConfig?.feature_flags?.features;


  // Define all possible card templates
  const CARD_TEMPLATES = {
    document: {
      title: featureFlags?.negotiations === true ? "Tendering" : "Documents",
      subtitle: featureFlags?.rfpReview === true ? "Manage your procurement documents and RFPs" : "Manage your contracts and documents",
      icon: <DescriptionIcon fontSize="large" />,
    },
    contracting: {
      title: "Contracting", 
      subtitle: "Extract key insights and attributes from your contracts",
      icon: <AssignmentIcon fontSize="large" />,
    },
    negotiation: {
      title: "Negotiation",
      subtitle: "Manage supplier negotiations and stakeholders", 
      icon: <HandshakeIcon fontSize="large" />,
    },
    monitoring: {
      title: "Monitoring",
      subtitle: "Track opportunities and ensure contract compliance",
      icon: <TrendingUpIcon fontSize="large" />,
    }
  };

  // Define all possible features
  const FEATURE_DEFINITIONS = {
    documentManagement: {
      title: "Document Management",
      description: "Upload and manage your contracts.",
      icon: <StorageIcon />,
      path: "/homepage/management",
    },
    rfpReview: {
      title: "RFP Review", 
      description: "Scan your procurement documents for compliance and potential savings.",
      icon: <AssignmentTurnedInIcon />,
      path: "/homepage/contract-review",
    },
    negotiations: {
      title: "RFP Negotiations",
      description: "Manage and track RFP negotiations with suppliers and stakeholders.",
      icon: <HandshakeIcon />,
      path: "/homepage/negotiations", 
    },
    contractInsights: {
      title: "Contract Insights",
      description: "Extract insights using our GenAI chatbot.",
      icon: <VectorIcon />,
      path: "/homepage/intelligence",
    },
    contractAttributes: {
      title: "Contract Attributes", 
      description: "View data extracted from contract documents for Legal and Procurement use cases.",
      icon: <AttributesIcon />,
      path: "/homepage/attributes",
    },
    opportunities: {
      title: "Opportunities",
      description: "Review system identified opportunities and act on value leakage right away.",
      icon: <FindInPageIcon />,
      path: "/homepage/opportunities",
    }
  };


  // Define the business rules of showing which feature on the main page from the feature flags 
  const CARD_RULES = [
    {
      // Rule 1: When to show Document upload card
      cardTemplate: 'document',
      condition: () => true,
      features: [
        { feature: 'documentManagement', condition: () => true }, // Always show
        { feature: 'rfpReview', condition: (flags: typeof featureFlags) => (flags?.rfpReview ?? false) === true }
      ]
    },
    {
      // Rule 2: When to show Negotiation card
      cardTemplate: 'negotiation', 
      condition: (flags: typeof featureFlags) => (flags?.negotiations ?? false) === true,
      features: [
        { feature: 'negotiations', condition: () => true }
      ]
    },
    {
      // Rule 3: When to show Contracting card  
      cardTemplate: 'contracting',
      condition: () => true,
      features: [
        { feature: 'contractInsights', condition: () => true }, // Always show
        { feature: 'contractAttributes', condition: () => true } // Always show
      ]
    },
    {
      // Rule 4: When to show Monitoring card
      cardTemplate: 'monitoring',
      condition: (flags: typeof featureFlags) => (flags?.opportunities ?? false) === true, 
      features: [
        { feature: 'opportunities', condition: () => true }
      ]
    }
  ];

  // Simple engine that applies the rules (developers rarely need to touch this)
  function buildCategoriesFromRules(uiConfig: UIConfig | null) {
    // Return empty array if uiConfig is not available yet
    if (!uiConfig?.feature_flags) {
      return [];
    }
    
    const { features } = uiConfig.feature_flags;
    
    return CARD_RULES
      .filter(rule => rule.condition(features)) // Only include cards that pass their condition
      .map(rule => {
        const cardTemplate = CARD_TEMPLATES[rule.cardTemplate as keyof typeof CARD_TEMPLATES];
        const enabledFeatures = rule.features
          .filter(featureRule => featureRule.condition(features))
          .map(featureRule => FEATURE_DEFINITIONS[featureRule.feature as keyof typeof FEATURE_DEFINITIONS]);
        
        return {
          ...cardTemplate,
          features: enabledFeatures
        };
      });
  }

  const categories = buildCategoriesFromRules(uiConfig);
  const homepageSubHeading =
    uiConfig?.subHeadingText && uiConfig.subHeadingText.trim() !== ""
      ? uiConfig.subHeadingText
      : "Interrogating contracts made seamless.";

  const fetchPBIConfig = async () => {
    const resp = await getPBIConfig();
    if (resp.success) {
      localStorage.setItem("pbiconfig", JSON.stringify(resp.data));
    } else {
      dispatch(
        showSnackbar({
          message: `${resp.message}`,
          severity: "error",
        }),
      );
    }
  }

  return (
    <Grid sx={{ background: "#fff" }}>
      {/* Header Section */}
      <Card
        sx={{
          backgroundImage: `url(${
            (uiConfig?.login?.backgroundPhoto && uiConfig.login.backgroundPhoto !== "") 
              ? uiConfig.login.backgroundPhoto 
              : (frontendSettings && frontendSettings.login.loginPhoto)
          })`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          borderRadius: 3,
          padding: "10px",
          textAlign: "left",
          color: "#fff",
          height: "250px",
          display: "flex",
          mt: 8,
          mb: 2,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            fontWeight="bold"
            align="left"
            sx={{ color: theme.palette.primary.main }}
          >
            Welcome to {uiConfig?.title || "ContractIQ"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, color: theme.palette.primary.main }}
          >
            {homepageSubHeading}
            <br />
          </Typography>
        </CardContent>
      </Card>

      {/* Explore Section */}
      <Box sx={{ padding: 4, backgroundColor: "#f4f4f4" }}>
        <Box
          sx={{ padding: 2, backgroundColor: "#fff", borderRadius: 2 }}
        >
          {/* Main Title */}
          <Typography
            variant="h5"
            gutterBottom
            align="center"
            color="primary"
            sx={{ pb: 2 }}
          >
            Let's explore the application
          </Typography>

          {/* Categories */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
            {categories.map((category, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  borderColor: (theme: any) => theme.palette.primary.main,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 20px rgba(119, 71, 194, 0.2)',
                    borderColor: (theme: any) => theme.palette.secondary.main,
                  },
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <CardContent>
                  {/* Category Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1 }}>
                    <Box
                      sx={{
                        backgroundColor: (theme: any) => `${theme.palette.primary.light}30`,
                        borderRadius: '50%',
                        width: 60,
                        height: 60,
                        minWidth: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        color: (theme: any) => theme.palette.primary.main,
                      }}
                    >
                      {category.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" color="primary" fontWeight="600">
                        {category.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {category.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Features */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {category.features.map((feature, featureIndex) => (
                      <Box
                        key={featureIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          p: 1.5,
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: (theme: any) => `${theme.palette.primary.light}20`,
                            transform: 'scale(1.02)',
                          },
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                        }}
                        onClick={() => feature.path && navigate(feature.path)}
                      >
                        <Box
                          sx={{
                            color: (theme: any) => theme.palette.secondary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        >
                          {React.cloneElement(feature.icon, {
                            width: 24,
                            height: 24
                          })}
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {feature.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              color: (theme: any) => theme.palette.primary.main,
                              fontWeight: 'bold',
                              opacity: 0.7,
                              transition: 'all 0.2s ease',
                              ml: 1,
                              '&:hover': {
                                opacity: 1,
                                transform: 'translateX(3px)',
                              },
                            }}
                          >
                            →
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default Homepage;
